// eslint-disable-next-line
import MyKoinosWallet from "@roamin/my-koinos-wallet-sdk";
import * as kondor from "kondor-js";
import { get as _get } from "lodash";
import { useSnackbar } from "notistack";
import { Fragment, useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import {
  FormControlLabel,
  Checkbox,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { initializeWalletConnect, WALLET_CONNECT_METHODS } from "../../../helpers/wallets";

// Actions
import { setModal, setModalData } from "../../../redux/actions/modals";
import { setNetwork } from "../../../redux/actions/settings";
import { setConnected, setWallet, setWalletName } from "../../../redux/actions/wallet";
import { useWalletSession } from "../../../hooks/useWallet";

// helpers
import { defaultProvider } from "../../../helpers/contracts";
import { MKW_CONNECTOR_URL } from "../../../helpers/session";
// components
import walletconnect from "../../../assets/images/walletconnect.svg";
import mkwLogo from "../../../assets/images/mkw-logo.png";
import KondorLogo from "../../../assets/images/kondor.svg";
import { CONFIG_BASE } from "../../../constants/configs";
import ModalHeader from "./../ModalHeader";
// constants
import { CHAIN_IDS_TO_NAMES, CHAINS_IDS } from "./../../../constants/chains";

const ModalConnect = () => {
  // Dispatch to call actions
  const dispatch = useDispatch();

  // selector
  const currentModal = useSelector((state) => state.modals.modal);
  const Snackbar = useSnackbar();
  const { setSession, clearSession } = useWalletSession();

  // states
  const [loading, setLoading] = useState(false);
  const [backdrop, setBackdrop] = useState(false);
  const [, setSelectedWallet] = useState("");

  const [termsAccepted, setTermsAccepted] = useState(false);

  const closeModal = () => {
    dispatch(setModal(null));
    dispatch(setModalData(null));
  };

  // wallets configs
  const resetWalletState = () => {
    clearSession();
    dispatch(setWallet(null));
    dispatch(setWalletName(null));
    dispatch(setConnected(false));
    dispatch(setNetwork(null));
  };

  const actionClose = (snackbarId) => (
    <Fragment>
      <Button
        style={{ color: "white" }}
        onClick={() => {
          Snackbar.closeSnackbar(snackbarId);
        }}
      >
        Close
      </Button>
    </Fragment>
  );

  const closeBackdrop = () => {
    setBackdrop(false);
  };

  const KondorWallet = () => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      let timeOut = setTimeout(() => reject("Error Connect"), 1000 * 60);
      if (kondor) {
        try {
          closeModal();
          // use default provider
          let provider = defaultProvider;
          let wallet = await kondor.getAccounts();
          let signer = await kondor.getSigner(_get(wallet, "[0].address", ""), {
            providerPrepareTransaction: provider,
          });
          let chain_id = await kondor.provider.getChainId();
          wallet.object = null;
          // set connection
          setSession({ provider, signer, instance: null });
          dispatch(setWallet(wallet));
          dispatch(setWalletName("kondor"));
          dispatch(setConnected(true));
          dispatch(setModal(null));
          dispatch(setModalData(null));
          // check chain
          if (CHAIN_IDS_TO_NAMES[chain_id] == undefined) {
            chain_id = CHAINS_IDS.UNSUPPORTED;
          }
          dispatch(setNetwork(chain_id));
        } catch (e) {
          console.log(e);
          resetWalletState();
        }
        if (timeOut) {
          clearTimeout(timeOut);
        }
        resolve();
      }
    });
  };

  const MyKW = () => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      let timeOut = setTimeout(() => reject("Error, cannot Connect"), 1000 * 60);
      try {
        const mkw = new MyKoinosWallet(MKW_CONNECTOR_URL);
        const connected = await mkw.connect();
        if (connected) {
          closeModal();
          // request permissions to access some of the My Koinos Wallet APIs
          // awaited so a refusal ("request was cancelled") reaches the catch below
          await mkw.requestPermissions({
            accounts: ["getAccounts"],
            provider: ["getChainId", "readContract", "wait", "getAccountRc", "getTransactionsById"],
            signer: ["prepareTransaction", "signMessage", "signAndSendTransaction"],
          });
          // get data from connected wallet
          let wallet = await mkw.getAccounts();
          const provider = await mkw.getProvider();
          const signer = await mkw.getSigner(_get(wallet, "[0].address", ""));
          let chain_id = await provider.getChainId();

          // set connection
          setSession({ provider, signer, instance: mkw });
          dispatch(setWallet(wallet));
          dispatch(setWalletName("mkw"));
          dispatch(setConnected(connected));
          dispatch(setModal(null));
          dispatch(setModalData(null));

          // check chain
          if (CHAIN_IDS_TO_NAMES[chain_id] == undefined) {
            chain_id = CHAINS_IDS.UNSUPPORTED;
          }
          dispatch(setNetwork(chain_id));
        } else {
          Snackbar.enqueueSnackbar(
            <span>
              <Typography variant="h6">MKW Could not connect - Check that cross-site cookies are activated</Typography>
            </span>,
            {
              variant: "error",
              persist: false,
              action: actionClose,
            }
          );
          // throw new Error("MKW could not connect")
        }
      } catch (e) {
        // if user declines mkw access "request was cancelled" will be returned
        if (e !== "request was cancelled") {
          Snackbar.enqueueSnackbar(
            <span>
              <Typography variant="h6">MKW Could not connect</Typography>
            </span>,
            {
              variant: "error",
              persist: false,
              action: actionClose,
            }
          );
        }
        resetWalletState();
      }

      if (timeOut) {
        clearTimeout(timeOut);
      }
      resolve();
    });
  };

  const WC = () => {
    return new Promise((resolve, reject) => {
      // create WalletConnectKoinos
      const walletConnectKoinos = initializeWalletConnect();
      // initiate connection with a wallet
      try {
        walletConnectKoinos
          .connect([CONFIG_BASE.wallet.walletconnect_network_id], [...WALLET_CONNECT_METHODS])
          .then((accounts) => {
            if (accounts) {
              const provider = defaultProvider;
              const signer = walletConnectKoinos.getSigner(
                accounts[0],
                provider,
                CONFIG_BASE.wallet.walletconnect_network_id
              );
              const connected = accounts ? true : false;
              setSession({ provider, signer, instance: walletConnectKoinos });
              dispatch(setWallet([{ name: "walletconnect", address: accounts[0] }]));
              dispatch(setWalletName("walletconnect"));
              dispatch(setModal(null));
              dispatch(setModalData(null));
              dispatch(setConnected(connected));
              resolve(true);
            }
          })
          .catch((e) => {
            console.log(e);
          });
      } catch (e) {
        setBackdrop(false);
        setSelectedWallet("");
        setLoading(false);
        resetWalletState();
        walletConnectKoinos.disconnect();
        reject(e);
      }
    });
  };

  // methods
  const selectWallet = async (typed) => {
    setLoading(true);
    try {
      switch (typed) {
        case "kondor-wallet":
          setSelectedWallet("kondor");
          await KondorWallet();
          break;
        case "mkw-wallet":
          setSelectedWallet("mkw");
          await MyKW();
          break;
        case "wallet-connect":
          setSelectedWallet("walletconnect");
          setBackdrop(true);
          closeModal();
          setLoading(false);
          await WC();
          break;
        default:
          break;
      }
    } catch (e) {
      console.log(e);
      Snackbar.enqueueSnackbar(
        <span>
          <Typography variant="h6">Could not connect to wallet</Typography>
        </span>,
        {
          variant: "error",
          persist: false,
          action: actionClose,
        }
      );
    }
    setLoading(false);
  };

  // the wallets on offer; Kondor is a desktop extension
  const wallets = [
    ...(isMobile ? [] : [{ id: "kondor-wallet", name: "Kondor Wallet", logo: KondorLogo, alt: "kondor wallet" }]),
    { id: "mkw-wallet", name: "My Koinos Wallet", logo: mkwLogo, alt: "My Koinos Wallet wallet logo" },
    { id: "wallet-connect", name: "Wallet Connect", logo: walletconnect, alt: "Walletconnect logo" },
  ];

  const StepTitle = ({ step, done, children }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
      <Box
        aria-hidden
        sx={{
          width: 24,
          height: 24,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: "50%",
          fontSize: "0.8125rem",
          fontWeight: 600,
          bgcolor: done ? "primary.main" : "secondary.dark",
          color: done ? "primary.contrastText" : "text.primary",
          transition: "background-color 160ms ease",
        }}
      >
        {done ? <CheckIcon sx={{ fontSize: 16 }} /> : step}
      </Box>
      <Typography sx={{ fontWeight: 600 }}>{children}</Typography>
    </Box>
  );

  const acceptTerms = () => (
    <Box component="section">
      <StepTitle step={1} done={termsAccepted}>
        Accept the terms
      </StepTitle>
      <FormControlLabel
        sx={{
          m: 0,
          width: "100%",
          gap: 0.5,
          pr: 1.5,
          borderRadius: "12px",
          bgcolor: "background.light",
          "& .MuiFormControlLabel-label": { color: "text.secondary", fontSize: "0.875rem" },
        }}
        control={
          <Checkbox
            checked={termsAccepted}
            onChange={() => setTermsAccepted(!termsAccepted)}
            slotProps={{
              input: { "aria-label": "Terms and Privacy consent Checkbox" },
            }}
          />
        }
        label={
          <>
            I accept the{" "}
            <Link target={"_blank"} href="https://koindx.com/terms-of-use">
              Terms of use
            </Link>{" "}
            and{" "}
            <Link href="https://koindx.com/privacy" target={"_blank"}>
              Privacy policy
            </Link>
            .
          </>
        }
      />
    </Box>
  );

  const selectWalletList = () => (
    <Box component="section" sx={{ mt: 3 }}>
      <StepTitle step={2}>Choose a wallet</StepTitle>
      <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {wallets.map((wallet) => (
          <ListItem key={wallet.id} disablePadding>
            <ListItemButton
              sx={{
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: "12px",
                border: 1,
                borderColor: "divider",
                "&:hover": { borderColor: "secondary.dark", backgroundColor: "background.light" },
                "&.Mui-disabled": { opacity: 0.45, filter: "grayscale(1)" },
              }}
              disabled={!termsAccepted}
              onClick={() => selectWallet(wallet.id)}
            >
              <ListItemIcon sx={{ minWidth: 0 }}>
                <img src={wallet.logo} width="32" height="32" style={{ display: "block" }} alt={wallet.alt} />
              </ListItemIcon>
              <ListItemText primary={wallet.name} slotProps={{ primary: { sx: { fontWeight: 500 } } }} />
              <ChevronRightIcon sx={{ color: "text.secondary" }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Typography
        variant="body2"
        aria-hidden={termsAccepted}
        sx={{ color: "text.secondary", mt: 1.5, visibility: termsAccepted ? "hidden" : "visible" }}
      >
        Accept the terms to choose a wallet.
      </Typography>
    </Box>
  );

  const loadingWallet = () => (
    <Box sx={{ textAlign: "center", py: 1 }}>
      <CircularProgress size={32} sx={{ mb: 2 }} />
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Waiting for your wallet</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Approve the connection in your wallet. If you do not have Kondor or My Koinos Wallet installed, you can get one
        below.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {isMobile ? null : (
          <Button
            variant="outlined"
            fullWidth
            href="https://chrome.google.com/webstore/detail/kondor/ghipkefkpgkladckmlmdnadmcchefhjl"
            target="_blank"
          >
            Install Kondor Wallet
          </Button>
        )}
        <Button variant="outlined" fullWidth component={Link} href="https://mykw.vercel.app" target="_blank">
          Use My Koinos Wallet
        </Button>
        <Button
          fullWidth
          color="error"
          onClick={() => {
            setLoading(false);
            resetWalletState();
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {loading && setSelectedWallet == "walletconnect" ? (
        <Backdrop sx={{ color: "#fff", zIndex: () => 0 }} open={backdrop} onClick={closeBackdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <Modal open={currentModal === "Connect"} onClose={closeModal} sx={{ display: "flex", p: 2 }}>
          <Box sx={{ width: "100%", maxWidth: 440, m: "auto", maxHeight: "calc(100vh - 32px)", overflowY: "auto" }}>
            <ModalHeader title={"Connect a wallet"} closeFunction={closeModal} backArrow={null} />
            <Box sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
              {loading ? (
                loadingWallet()
              ) : (
                <>
                  {acceptTerms()}
                  {selectWalletList()}
                </>
              )}
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default ModalConnect;
